import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ButtonComponent } from '../../button/button.component';
import { InputComponent } from '../../input/input.component';
import { SelectComponent } from '../../select/select.component';
import { UniversityEducationExtended } from '../../../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { CvOptions } from '../../../models/cv-options.model';
import { AttachmentService } from '../../../../../../../libs/shared/src/lib/services/attachment.service';
import { catchError, finalize, map, of } from 'rxjs';
import { BodyAttachmentResponse } from '../../../../../../../libs/shared/src/lib/models/attachment.model';
import { FileUploadService } from '../../../../../../../libs/shared/src/lib/services/file-upload/file-upload.service';
import { AttachmentData } from '../../../../../../../libs/shared/src/lib/services/file-upload/file-upload-types';
import { InputCheckboxComponent } from '../../input-checkbox/input-checkbox.component';
import { formatDateForInput } from '../../../utils/date-utils';
import { dateRangeValidator } from '../../../utils/date-range.validator';
import { FIELD_VALIDATION } from '../../../../../../../libs/shared/src/lib/constants/validation-constants';

@Component({
  selector: 'app-university-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, InputComponent, InputCheckboxComponent, SelectComponent, TranslocoPipe],
  templateUrl: './university-form.component.html',
  styleUrls: ['./university-form.component.scss'],
})
export class UniversityFormComponent implements OnInit, OnChanges {
  @Input() editingUniversity: UniversityEducationExtended | null = null;
  @Input() editingIndex: number | null = null;
  @Input() cvOptions: CvOptions | null = null;
  @Input() isAuthenticated = false;

  @Output() save = new EventEmitter<Partial<UniversityEducationExtended>>();
  @Output() cancel = new EventEmitter<void>();

  universityForm: FormGroup;
  isUploading = false;
  isCurrentlyStudying = false;
  fileError: string | null = null;
  educationLevelOptions: { value: number; label: string }[] = [];

  // Expose validation constants to template
  readonly VALIDATION = FIELD_VALIDATION;

  // Single attachment object to handle all file-related data
  attachment: AttachmentData = {
    file: undefined,
    fileName: '',
    fileSize: '',
    fileType: ''
  };

  // List for duplicate checking
  existingFiles: AttachmentData[] = [];

  constructor(
    private fb: FormBuilder,
    private attachmentService: AttachmentService,
    private fileUploadService: FileUploadService,
    private translocoService: TranslocoService
  ) {
    this.universityForm = this.createForm();
  }

  ngOnInit(): void {
    this.buildEducationLevelOptions();
    this.initFormValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cvOptions']) {
      this.buildEducationLevelOptions();
    }
    if (changes['editingUniversity'] && changes['editingUniversity'].currentValue) {
      this.initFormValues();
    }
  }

  private buildEducationLevelOptions(): void {
    this.educationLevelOptions = (this.cvOptions?.educationLevels || [])
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(level => ({
        value: level.id,
        label: this.translocoService.translate('jobEducationLevels.' + level.educationKey)
      }));
  }

  /**
   * Create the form with validation using SSOT validation constants
   */
  createForm(): FormGroup {
    return this.fb.group(
      {
        universityName: ['', [Validators.required, Validators.maxLength(FIELD_VALIDATION.UNIVERSITY_NAME_MAX_LENGTH)]],
        fieldOfStudy: ['', [Validators.required, Validators.maxLength(FIELD_VALIDATION.FIELD_OF_STUDY_MAX_LENGTH)]],
        educationLevelID: ['', Validators.required],
        city: ['', [Validators.maxLength(FIELD_VALIDATION.CITY_MAX_LENGTH)]],
        country: ['', [Validators.maxLength(FIELD_VALIDATION.COUNTRY_MAX_LENGTH)]],
        startDate: ['', Validators.required],
        endDate: ['', Validators.required],
        isCurrentlyStudying: [false],
        attachmentId: [null],
        attachmentUrl: [null],
      },
      { validators: dateRangeValidator({ ongoingKey: 'isCurrentlyStudying' }) }
    );
  }

  initFormValues(): void {
    if (this.editingUniversity) {
      // Handle both frontend and backend data structures
      const isCurrentlyStudying = this.editingUniversity.current || false;

      // Handle attachment from either frontend or backend structure
      let attachmentId = null;

      // Check for attachment data
      if (this.editingUniversity.attachmentID && this.editingUniversity.candidateAttachment) {
        attachmentId = this.editingUniversity.attachmentID;

        // Set file information in the attachment object
        this.attachment.fileName = this.editingUniversity.candidateAttachment.fileName || '';
        this.attachment.fileType = this.editingUniversity.candidateAttachment.fileType || '';

        // Set file size if available from backend
        if (this.editingUniversity.candidateAttachment.fileSize) {
          const fileSize = this.editingUniversity.candidateAttachment.fileSize;
          this.attachment.fileSize = typeof fileSize === 'number' ? this.formatFileSize(fileSize) : fileSize.toString();
        }
      }

      this.universityForm.patchValue({
        universityName: this.editingUniversity.universityName || '',
        fieldOfStudy: this.editingUniversity.fieldOfStudy || '',
        educationLevelID: this.editingUniversity.educationLevelID || '',
        city: this.editingUniversity.city || '',
        country: this.editingUniversity.country || '',
        startDate: formatDateForInput(this.editingUniversity.startDate) || '',
        endDate: formatDateForInput(this.editingUniversity.endDate) || '',
        isCurrentlyStudying: isCurrentlyStudying,
        attachmentId: attachmentId
      });

    }

    // Handle currently studying checkbox
    this.isCurrentlyStudying = this.universityForm.get('isCurrentlyStudying')?.value || false;

    // Disable end date if currently studying
    if (this.isCurrentlyStudying) {
      this.universityForm.get('endDate')?.disable();
    }

    // Listen for changes to the isCurrentlyStudying control
    this.universityForm.get('isCurrentlyStudying')?.valueChanges.subscribe((value) => {
      this.isCurrentlyStudying = value;
      if (value) {
        this.universityForm.get('endDate')?.disable();
        this.universityForm.get('endDate')?.setValue('');
      } else {
        this.universityForm.get('endDate')?.enable();
      }
    });
  }

  // Date formatting methods moved to date-utils.ts

  // Second date formatting method moved to date-utils.ts

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Get education upload configuration
      const config = this.fileUploadService.getDefaultConfig('education');

      // Validate file using unified service
      const validation = this.fileUploadService.validateFile(file, config);
      if (!validation.valid) {
        this.showFileError(validation.error || this.translocoService.translate('profile.fileUpload.errors.invalidFile'));
        return;
      }

      // Check for duplicates
      if (this.fileUploadService.checkDuplicateFile(file, this.existingFiles)) {
        this.showFileError(this.translocoService.translate('profile.fileUpload.errors.duplicate'));
        return;
      }

      // Store file information using unified service
      const fileInfo = this.fileUploadService.extractFileInfo(file);
      this.attachment = {
        file: file,
        fileName: fileInfo.fileName,
        fileSize: fileInfo.fileSize,
        fileType: fileInfo.fileType
      };
      this.fileError = null;

      // Upload the file immediately
      this.uploadFile(file);
    }
  }

  formatFileSize(bytes: number): string {
    return this.fileUploadService.formatFileSize(bytes);
  }

  /**
   * Display file error message
   * @param message Error message to display
   */
  private showFileError(message: string): void {
    this.fileError = message;
    // Reset file data
    this.attachment = {
      file: undefined,
      fileName: '',
      fileSize: '',
      fileType: ''
    };
  }

  /**
   * Remove the uploaded file
   */
  removeFile(): void {
    // Reset attachment object
    this.attachment = {
      file: undefined,
      fileName: '',
      fileSize: '',
      fileType: ''
    };

    this.fileError = null;
    this.universityForm.patchValue({
      attachmentId: null,
      attachmentUrl: null
    });
  }

  /**
   * Upload the certificate file to the server using unified service
   * @param file The file to upload
   */
  async uploadFile(file: File): Promise<void> {
    if (!file) {
      return;
    }

    this.isUploading = true;
    this.fileError = null;

    try {
      // Convert file to blob using unified service
      const blob = await this.fileUploadService.fileToBlob(file);

      // Use unified service to perform upload
      const uploadObservable = this.fileUploadService.performUpload(
        blob,
        file,
        this.isAuthenticated,
        this.attachmentService
      );

      uploadObservable
        .pipe(
          finalize(() => {
            this.isUploading = false;
          })
        )
        .subscribe({
          next: (response) => {
            const result = this.fileUploadService.handleUploadResponse(response, file, this.isAuthenticated);

            if (result.success && result.data) {
              if (result.isUrl) {
                // Non-authenticated upload - store URL
                this.universityForm.patchValue({
                  attachmentUrl: result.data.url
                });
              } else {
                // Authenticated upload - store ID
                this.universityForm.patchValue({
                  attachmentId: result.data.id
                });
              }

              // Add to existing files list for duplicate checking
              this.existingFiles.push(this.attachment);
            } else {
              this.showFileError(result.error || this.translocoService.translate('profile.fileUpload.errors.uploadFailed'));
            }
          },
          error: (error: any) => {
            let errorMessage = this.translocoService.translate('profile.fileUpload.errors.uploadError');

            if (error.status === 413) {
              errorMessage = this.translocoService.translate('profile.fileUpload.errors.tooLarge');
            } else if (error.status === 415) {
              errorMessage = this.translocoService.translate('profile.fileUpload.errors.unsupportedType');
            } else if (error.status === 0) {
              errorMessage = this.translocoService.translate('profile.fileUpload.errors.networkError');
            } else if (error.error?.message) {
              errorMessage = error.error.message;
            }

            this.showFileError(errorMessage);
            console.error('Error uploading file:', error);
          }
        });
    } catch (error) {
      this.isUploading = false;
      this.showFileError(this.translocoService.translate('profile.fileUpload.errors.processingError'));
      console.error('Error processing file:', error);
    }
  }

  onSubmit(): void {
    if (this.universityForm.invalid || this.isUploading) {
      this.universityForm.markAllAsTouched();
      return;
    }

    const formValue = this.universityForm.getRawValue();

    // Prepare university data for API submission
    const universityData: Partial<UniversityEducationExtended> = {
      universityName: formValue.universityName,
      fieldOfStudy: formValue.fieldOfStudy,
      educationLevelID: formValue.educationLevelID,
      city: formValue.city,
      country: formValue.country,
      startDate: formValue.startDate, // Date formatting handled in UserProfileService
      isCurrentlyStudying: formValue.isCurrentlyStudying,
      current: formValue.isCurrentlyStudying, // Backend property
    };

    // Only include end date if not currently studying
    if (!formValue.isCurrentlyStudying && formValue.endDate) {
      universityData.endDate = formValue.endDate; // Date formatting handled in UserProfileService
    }

    // Handle attachment for both auth types using unified service approach
    if (formValue.attachmentId) {
      // Authenticated user - has real attachment ID
      universityData.attachmentID = formValue.attachmentId;
      universityData.candidateAttachment = this.fileUploadService.createAttachmentObject(
        this.attachment.file || new File([], this.attachment.fileName || ''),
        { id: formValue.attachmentId },
        true
      );
    } else if (formValue.attachmentUrl) {
      // Non-authenticated user - has URL from upload
      universityData.candidateAttachment = this.fileUploadService.createAttachmentObject(
        this.attachment.file || new File([], this.attachment.fileName || ''),
        formValue.attachmentUrl,
        false
      );
    }

    // Preserve ID if editing
    if (this.editingUniversity && this.editingUniversity.id) {
      universityData.id = this.editingUniversity.id;
    }

    // Emit save event with form data
    this.save.emit(universityData);
  }

}
